import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, CreditCard, XCircle, CheckCircle2, AlertCircle, Lock, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { generateOrderNumber } from '../utils/orderNumber';
import { openTranzilaPayment } from '../utils/tranzila';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
const PAYMENT_WEBHOOK_URL = 'https://hook.eu2.make.com/ijcmimuvqdy2jlm4dw9srvba9g2ndkub';
const CANCEL_WEBHOOK_URL = 'https://hook.eu2.make.com/u9r2klw104wru5uhotc67rlx1tr3b1gi';
const UPGRADE_PRICE = 240;
export function AdditionalPaymentPage() {
  const [activeTab, setActiveTab] = useState<'payment' | 'cancel' | null>(null);
  // Payment State
  const [payForm, setPayForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showIframe, setShowIframe] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Cancel State
  const [cancelForm, setCancelForm] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
    otherReason: ''
  });
  const [isSubmittingCancel, setIsSubmittingCancel] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  // ============================================
  // TEST MODE
  // ============================================
  const [isTestMode, setIsTestMode] = useState(false);
  useEffect(() => {
    document.title = 'עדכון תאריכים - Water Tribe Fest 2026';
    const urlParams = new URLSearchParams(window.location.search);
    const testModeParam = urlParams.get('test_mode');
    if (testModeParam === 'watertribe2026') {
      setIsTestMode(true);
      localStorage.setItem('test_mode_active', 'true');
      console.log('🧪 TEST MODE ACTIVATED - Price set to ₪1');
    } else {
      const storedTestMode = localStorage.getItem('test_mode_active');
      if (storedTestMode === 'true') {
        setIsTestMode(true);
        console.log('🧪 TEST MODE ACTIVE (from localStorage)');
      }
    }
  }, []);
  // Derived price based on test mode
  const finalPrice = isTestMode ? 1 : UPGRADE_PRICE;
  const finalPriceDisplay = isTestMode ? '1' : UPGRADE_PRICE.toString();
  // --- Handlers ---
  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const orderNumber = generateOrderNumber();
    const submissionData = {
      name: payForm.name,
      email: payForm.email,
      phone: payForm.phone,
      orderNumber: orderNumber,
      type: 'תוספת תשלום שדרוג',
      price: finalPrice.toString(),
      source: 'דף שדרוג תאריכים',
      webhookUrl: PAYMENT_WEBHOOK_URL
    };
    localStorage.setItem('pendingOrder', JSON.stringify({
      orderNumber,
      name: payForm.name,
      email: payForm.email,
      timestamp: Date.now()
    }));
    localStorage.setItem('pendingRegistrationData', JSON.stringify(submissionData));
    setShowIframe(true);
    setTimeout(() => {
      openTranzilaPayment({
        sum: finalPrice,
        contact: payForm.name,
        email: payForm.email,
        phone: payForm.phone,
        pdesc: 'תוספת תשלום - שדרוג תאריכים',
        success_url: `${window.location.origin}/tranzila-success`,
        fail_url: window.location.href,
        u1: orderNumber,
        u2: 'תוספת תשלום',
        u3: finalPrice.toString()
      });
      setTimeout(() => {
        iframeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }, 100);
  };
  const handleCancelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingCancel(true);
    setCancelError(false);
    try {
      const payload = {
        type: 'בקשת ביטול',
        name: cancelForm.name,
        email: cancelForm.email,
        phone: cancelForm.phone,
        cancelReason: cancelForm.reason,
        cancelDetails: cancelForm.reason === 'אחר' ? cancelForm.otherReason : '',
        timestamp: new Date().toISOString()
      };
      const res = await fetch(CANCEL_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to submit cancellation');
      setCancelSuccess(true);
    } catch (err) {
      setCancelError(true);
    } finally {
      setIsSubmittingCancel(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex flex-col" dir="rtl" data-id="element-706" data-mp="1fcoovsdrmdp2">
      <Navigation data-id="element-707" data-mp="1oaik0zzsn1an" />

      {/* TEST MODE INDICATOR */}
      {isTestMode && <div className="fixed top-4 left-4 z-50 bg-yellow-500 text-black px-4 py-2 rounded-lg shadow-lg font-bold text-sm flex items-center gap-2" data-id="element-708" data-mp="34p0coxm3en12">
          <Zap className="w-4 h-4" data-id="element-709" data-mp="2zpnr0g6yjm02" />
          TEST MODE - ₪1
        </div>}

      <main className="flex-grow container mx-auto px-4 py-12 md:py-20 flex flex-col items-center justify-center" data-id="element-710" data-mp="3864tlvfvzv52">
        {/* Header Section */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-3xl w-full text-center mb-12" data-id="element-711" data-mp="3c6gen9c5vxh2">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-bold mb-6" data-id="element-712" data-mp="3qxfkiclm47wy">
            <Calendar className="w-5 h-5" data-id="element-713" data-mp="3vw8ye64ta0xn" />
            <span data-id="element-714" data-mp="3h7vyiy2lrxxv">עדכון חשוב</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-[#0A5F8F] mb-6 leading-tight" data-id="element-715" data-mp="3h731wpxcyotg">
            הפסטיבל שודרג לתאריכים
            <br data-id="element-716" data-mp="3m4w4za3d72pi" />
            <span className="text-cyan-500" data-id="element-717" data-mp="dr5q6cjjt8t0">19-21 בנובמבר 2026!</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-718" data-mp="dpchkrpdkeid">
            בעקבות המעבר לשיא העונה בכנרת — עם מזג אוויר מושלם וחוויה משודרגת —
            ישנה תוספת תשלום של{' '}
            <strong className="text-gray-900" data-id="element-719" data-mp="nerr9csbse0e">₪{finalPrice}</strong> למשתתף.
          </p>
        </motion.div>

        {/* Main Content Area */}
        <div className="max-w-4xl w-full" data-id="element-720" data-mp="ien6vboaqz4w">
          {/* Tabs Selection */}
          {!activeTab && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-721" data-mp="edbi18d8yhxr">
              {/* Payment Option */}
              <button onClick={() => setActiveTab('payment')} className="bg-white rounded-3xl p-8 shadow-xl border-2 border-transparent hover:border-cyan-400 transition-all group text-right flex flex-col h-full" data-id="element-722" data-mp="3vs8h8q2u26ss">
                <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-723" data-mp="3hw4q55n9aekj">
                  <CheckCircle2 className="w-8 h-8 text-cyan-600" data-id="element-724" data-mp="22bubryyj3fy8" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-725" data-mp="22dp25sskk09p">
                  אני ממשיך! 🎉
                </h2>
                <p className="text-gray-600 mb-8 flex-grow" data-id="element-726" data-mp="2bd2bmlgp0ouf">
                  מעולה! לחצו כאן להשלמת תוספת התשלום (₪{finalPrice}) והבטחת
                  מקומכם בתאריכים החדשים.
                </p>
                <div className="flex items-center text-cyan-600 font-bold gap-2 group-hover:gap-3 transition-all" data-id="element-727" data-mp="1ind8o2kfaeq6">
                  <span data-id="element-728" data-mp="1nmpxutyrnxvx">מעבר לתשלום</span>
                  <ArrowRight className="w-5 h-5 rotate-180" data-id="element-729" data-mp="1nkv8n3ggpjm1" />
                </div>
              </button>

              {/* Cancel Option */}
              <button onClick={() => setActiveTab('cancel')} className="bg-white rounded-3xl p-8 shadow-xl border-2 border-transparent hover:border-rose-400 transition-all group text-right flex flex-col h-full" data-id="element-730" data-mp="uyu2ptouie19">
                <div className="bg-rose-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-731" data-mp="3phjo16jijuth">
                  <XCircle className="w-8 h-8 text-rose-600" data-id="element-732" data-mp="2azni5tu0ojv" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-733" data-mp="3ue0matkffpka">
                  אני מעוניין לבטל 😔
                </h2>
                <p className="text-gray-600 mb-8 flex-grow" data-id="element-734" data-mp="c7m8qvq2lhmf">
                  מבינים לגמרי. לחצו כאן למילוי טופס קצר לביטול ההשתתפות וקבלת
                  החזר כספי.
                </p>
                <div className="flex items-center text-rose-600 font-bold gap-2 group-hover:gap-3 transition-all" data-id="element-735" data-mp="lxoy8lxzgdg0">
                  <span data-id="element-736" data-mp="h05mbsorccr8">לבקשת ביטול</span>
                  <ArrowRight className="w-5 h-5 rotate-180" data-id="element-737" data-mp="uzeqftzovgeu" />
                </div>
              </button>
            </motion.div>}

          {/* Payment Form */}
          <AnimatePresence mode="wait" data-id="element-738" data-mp="q253ckzrvgu6">
            {activeTab === 'payment' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-739" data-mp="19mn7vcjlchz9">
                <div className="bg-gradient-to-r from-[#0A5F8F] to-cyan-600 p-6 md:p-8 text-white flex items-center justify-between" data-id="element-740" data-mp="vmcfg52muvbz">
                  <div data-id="element-741" data-mp="gyhlymgswmxu">
                    <h2 className="text-2xl font-bold mb-2" data-id="element-742" data-mp="gz115vf2gx86">השלמת תשלום</h2>
                    <p className="text-cyan-100" data-id="element-743" data-mp="lxkpu3l1on48">
                      תוספת שדרוג תאריכים: ₪{finalPrice}
                    </p>
                  </div>
                  <button onClick={() => {
                setActiveTab(null);
                setShowIframe(false);
              }} className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors" data-id="element-744" data-mp="cvv155ijs2a5">
                    <ArrowRight className="w-6 h-6" data-id="element-745" data-mp="1ulf0xjjt2usu" />
                  </button>
                </div>

                <div className="p-6 md:p-8" data-id="element-746" data-mp="1gq33v5i6gs8r">
                  {!showIframe ? <form onSubmit={handlePaymentSubmit} className="space-y-6 max-w-md mx-auto" data-id="element-747" data-mp="1ln3abhixvx0e">
                      <div data-id="element-748" data-mp="2dcy01faplopl">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-749" data-mp="2dd7rl7bek3xe">
                          שם מלא
                        </label>
                        <input type="text" required value={payForm.name} onChange={e => setPayForm({
                    ...payForm,
                    name: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all outline-none" placeholder="ישראל ישראלי" data-id="element-750" data-mp="24l5spdgzevp8" />
                      </div>
                      <div data-id="element-751" data-mp="3b9ux6h03qtrn">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-752" data-mp="2nheeq5cgczrt">
                          אימייל (שאיתו נרשמתם)
                        </label>
                        <input type="email" required value={payForm.email} onChange={e => setPayForm({
                    ...payForm,
                    email: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all outline-none text-right" dir="ltr" placeholder="israel@example.com" data-id="element-753" data-mp="2jifa6lumv50w" />
                      </div>
                      <div data-id="element-754" data-mp="tr0lxizes0zz">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-755" data-mp="tq7mz46oyfse">
                          טלפון
                        </label>
                        <input type="tel" required value={payForm.phone} onChange={e => setPayForm({
                    ...payForm,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all outline-none text-right" dir="ltr" placeholder="050-0000000" data-id="element-756" data-mp="3s71rprkynfi8" />
                      </div>

                      <Button type="submit" className="w-full py-4 text-lg font-bold bg-gradient-to-r from-[#0A5F8F] to-cyan-600 hover:from-[#084b72] hover:to-cyan-700 rounded-xl shadow-lg flex items-center justify-center gap-2" data-id="element-757" data-mp="2wzsx1dxwwilz">
                        <CreditCard className="w-5 h-5" data-id="element-758" data-mp="2j8o1oo1kuchg" />
                        מעבר לתשלום מאובטח (₪{finalPrice})
                      </Button>
                    </form> : <div className="w-full" data-id="element-759" data-mp="29a872b3ccl37">
                      <div className="bg-gray-50 p-4 border border-gray-200 rounded-t-xl flex items-center justify-between mb-[-1px] relative z-10" data-id="element-760" data-mp="29bjauawsdsub">
                        <div className="flex items-center gap-2 text-gray-600" data-id="element-761" data-mp="209if5ih7beur">
                          <Lock className="w-4 h-4" data-id="element-762" data-mp="20bmser0zpq7p" />
                          <span className="text-sm font-medium" data-id="element-763" data-mp="2p17o3fajd86q">
                            תשלום מאובטח באמצעות טרנזילה
                          </span>
                        </div>
                        <div className="text-sm font-bold text-[#0A5F8F]" data-id="element-764" data-mp="2g8e1sxwo98jd">
                          ₪{finalPrice}
                        </div>
                      </div>
                      <iframe ref={iframeRef} name="tranzila" width="100%" height="600" frameBorder="0" allow="payment" className="w-full border border-gray-200 rounded-b-xl" data-id="element-765" data-mp="269ojx54rzo34"></iframe>
                    </div>}
                </div>
              </motion.div>}

            {/* Cancel Form */}
            {activeTab === 'cancel' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 max-w-2xl mx-auto w-full" data-id="element-766" data-mp="3vnbhqrx561eo">
                <div className="bg-gradient-to-r from-rose-500 to-red-600 p-6 md:p-8 text-white flex items-center justify-between" data-id="element-767" data-mp="23oquwgk6xiaj">
                  <div data-id="element-768" data-mp="1ypnqmyc1bg3m">
                    <h2 className="text-2xl font-bold mb-2" data-id="element-769" data-mp="1ys9wmtja1njq">בקשת ביטול</h2>
                    <p className="text-rose-100" data-id="element-770" data-mp="2cpf0cmviqabt">
                      נשמח להבין למה כדי להשתפר לפעם הבאה
                    </p>
                  </div>
                  {!cancelSuccess && <button onClick={() => setActiveTab(null)} className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors" data-id="element-771" data-mp="icp8sbsmhd7l">
                      <ArrowRight className="w-6 h-6" data-id="element-772" data-mp="2dgn7dk9m6x67" />
                    </button>}
                </div>

                <div className="p-6 md:p-8" data-id="element-773" data-mp="2ig03ywqafilz">
                  {cancelSuccess ? <div className="text-center py-8" data-id="element-774" data-mp="2nbyp8bgbkki6">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-775" data-mp="2rf3lvxdpxryw">
                        <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-776" data-mp="2wdey2erhkeka" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-777" data-mp="31bghj0stqfwa">
                        בקשתך התקבלה
                      </h3>
                      <p className="text-gray-600 text-lg mb-8" data-id="element-778" data-mp="3b2ujrgc5ttt4">
                        ניצור קשר בהקדם להשלמת תהליך הביטול וההחזר הכספי.
                      </p>
                      <Button variant="secondary" onClick={() => window.location.href = '/'} data-id="element-779" data-mp="3f693oi37yubu">
                        חזרה לדף הבית
                      </Button>
                    </div> : <form onSubmit={handleCancelSubmit} className="space-y-6" data-id="element-780" data-mp="13gawp3omhulc">
                      {cancelError && <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-start gap-3 border border-red-200" data-id="element-781" data-mp="zhlemxsn3ovd">
                          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-782" data-mp="1r6ndl41uj1o2" />
                          <p data-id="element-783" data-mp="1r8rfxhk3jsm8">
                            אירעה שגיאה בשליחת הבקשה. אנא נסו שוב או צרו קשר.
                          </p>
                        </div>}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-784" data-mp="1icigi2w4cu03">
                        <div data-id="element-785" data-mp="1degxfhyp0uom">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-786" data-mp="1ddfopk55gka2">
                            שם מלא
                          </label>
                          <input type="text" required value={cancelForm.name} onChange={e => setCancelForm({
                      ...cancelForm,
                      name: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none" data-id="element-787" data-mp="2635t31gzc7sz" />
                        </div>
                        <div data-id="element-788" data-mp="223kvrdp1s9rj">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-789" data-mp="1o6xooi3ds37w">
                            טלפון
                          </label>
                          <input type="tel" required value={cancelForm.phone} onChange={e => setCancelForm({
                      ...cancelForm,
                      phone: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none text-right" dir="ltr" data-id="element-790" data-mp="1s9u9mfge8bf0" />
                        </div>
                      </div>

                      <div data-id="element-791" data-mp="3hp52qu1uzod3">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-792" data-mp="3hpxzd273sxhi">
                          אימייל (שאיתו נרשמתם)
                        </label>
                        <input type="email" required value={cancelForm.email} onChange={e => setCancelForm({
                    ...cancelForm,
                    email: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none text-right" dir="ltr" data-id="element-793" data-mp="3rncf0zl7di8r" />
                      </div>

                      <div className="pt-4 border-t border-gray-100" data-id="element-794" data-mp="ozm4cusrb8ji">
                        <label className="block text-base font-bold text-gray-900 mb-4" data-id="element-795" data-mp="13p1pnv9iqlrt">
                          סיבת הביטול:
                        </label>
                        <div className="space-y-3" data-id="element-796" data-mp="1crt483xoxxno">
                          {['התאריך החדש לא נוח לי', 'העלות הנוספת', 'אחר'].map(reason => <label key={reason} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-797" data-mp="3eio3j5r3wez5">
                                <input type="radio" name="cancelReason" value={reason} required checked={cancelForm.reason === reason} onChange={e => setCancelForm({
                        ...cancelForm,
                        reason: e.target.value
                      })} className="w-5 h-5 text-rose-600 focus:ring-rose-500" data-id="element-798" data-mp="30dxhozpu47gh" />
                                <span className="text-gray-700 font-medium" data-id="element-799" data-mp="2cpe9slap5wox">
                                  {reason}
                                </span>
                              </label>)}
                        </div>
                      </div>

                      {cancelForm.reason === 'אחר' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} data-id="element-800" data-mp="4ed79yeyyl2x">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-801" data-mp="n5w8q0tchr2u">
                            פרטו בבקשה:
                          </label>
                          <textarea required value={cancelForm.otherReason} onChange={e => setCancelForm({
                    ...cancelForm,
                    otherReason: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none resize-none h-24" data-id="element-802" data-mp="idmlisjm7549" />
                        </motion.div>}

                      <Button type="submit" disabled={isSubmittingCancel} className="w-full py-4 text-lg font-bold bg-rose-600 hover:bg-rose-700 rounded-xl shadow-lg" data-id="element-803" data-mp="1sfa85w87glrb">
                        {isSubmittingCancel ? 'שולח...' : 'שלח בקשת ביטול'}
                      </Button>
                    </form>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>

      <Footer data-id="element-804" data-mp="2g170pou7ncq3" />
    </div>;
}